import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Circle, CheckCircle2, Bell, ExternalLink, Pencil, Trash2, Utensils, Calendar as CalIcon } from "lucide-react";
import { plannerAPI, formatTime } from "@/lib/planner";
import { toast } from "sonner";
import { EventDialog } from "@/pages/planner/PlannerCalendar";

export default function PlannerToday() {
  const [data, setData] = useState({ events: [], date: "" });
  const [loading, setLoading] = useState(true);
  const [editingEvent, setEditingEvent] = useState(null);

  const load = async () => {
    try {
      const d = await plannerAPI.today();
      setData(d);
    } catch { toast.error("Couldn't load today's schedule."); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const toggleDone = async (ev) => {
    try {
      const updated = await plannerAPI.updateEvent(ev.id, { done: !ev.done_at });
      setData({ ...data, events: data.events.map((e) => (e.id === ev.id ? updated : e)) });
    } catch { toast.error("Couldn't update."); }
  };
  const del = async (ev) => {
    if (!window.confirm(`Delete "${ev.title}"?`)) return;
    try {
      await plannerAPI.deleteEvent(ev.id);
      setData({ ...data, events: data.events.filter((e) => e.id !== ev.id) });
    } catch { toast.error("Couldn't delete."); }
  };

  const label = data.date ? new Date(data.date + "T00:00:00").toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" }) : "";

  return (
    <div data-testid="planner-today">
      <div className="flex items-baseline justify-between mb-6 flex-wrap gap-3">
        <div>
          <div className="text-[10px] tracking-[0.35em] text-[#A87F56]">TODAY</div>
          <h1 className="text-3xl sm:text-4xl text-[#2A2823] mt-1" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>{label}</h1>
        </div>
        <Link
          to="/planner/calendar?add=1"
          data-testid="planner-today-add"
          className="inline-flex items-center rounded-full bg-[#3E4A37] text-[#F7F3ED] hover:bg-[#2f3a2a] px-5 h-10 text-sm"
        >
          + Add an event
        </Link>
      </div>

      <div className="bg-white rounded-2xl p-6 border border-[#A87F56]/25">
        {loading ? (
          <div className="text-sm text-[#2A2823]/50 py-6 text-center">Loading…</div>
        ) : data.events.length === 0 ? (
          <div className="text-sm text-[#2A2823]/60 italic py-10 text-center">
            Nothing on the calendar for today.
          </div>
        ) : (
          <ul className="divide-y divide-[#A87F56]/15" data-testid="planner-today-list">
            {data.events.map((ev) => (
              <li key={ev.id} className="py-3 flex items-center gap-3" data-testid={`planner-today-row-${ev.id}`}>
                <div className="w-10 h-10 rounded-full bg-[#EDE5DA] flex items-center justify-center shrink-0">
                  {ev.event_type === "reminder" ? <Bell className="w-4 h-4 text-[#3E4A37]" /> : ev.event_type === "task" ? <CheckCircle2 className="w-4 h-4 text-[#3E4A37]" /> : <CalIcon className="w-4 h-4 text-[#3E4A37]" />}
                </div>
                <div className="text-xs tabular-nums text-[#2A2823]/70 w-20 shrink-0">
                  {ev.all_day ? "All day" : formatTime(ev.start_at)}
                </div>
                <div className={`flex-1 min-w-0 ${ev.done_at ? "line-through text-[#2A2823]/40" : "text-[#2A2823]"}`}>
                  <div className="truncate">{ev.title}</div>
                  {ev.notes && <div className="text-xs text-[#2A2823]/55 mt-0.5 line-clamp-1">{ev.notes}</div>}
                </div>
                {ev.remind_by_email && (
                  <span className="text-[10px] uppercase tracking-wider text-[#3E4A37] bg-[#3E4A37]/10 rounded-full px-2 py-0.5 shrink-0">
                    Email reminder
                  </span>
                )}
                <a
                  href={plannerAPI.eventIcsUrl(ev.id)}
                  target="_blank"
                  rel="noreferrer"
                  aria-label="Add to calendar"
                  data-testid={`planner-today-ics-${ev.id}`}
                  className="text-[#2A2823]/40 hover:text-[#3E4A37]"
                >
                  <ExternalLink className="w-4 h-4" />
                </a>
                <button
                  onClick={() => toggleDone(ev)}
                  data-testid={`planner-today-toggle-${ev.id}`}
                  className="text-[#3E4A37] hover:text-[#2f3a2a]"
                >
                  {ev.done_at ? <CheckCircle2 className="w-5 h-5" /> : <Circle className="w-5 h-5" />}
                </button>
                <button
                  onClick={() => setEditingEvent(ev)}
                  data-testid={`planner-today-edit-${ev.id}`}
                  aria-label="Edit"
                  className="text-[#2A2823]/40 hover:text-[#3E4A37]"
                >
                  <Pencil className="w-4 h-4" />
                </button>
                <button
                  onClick={() => del(ev)}
                  data-testid={`planner-today-delete-${ev.id}`}
                  aria-label="Delete"
                  className="text-[#2A2823]/40 hover:text-[#8a3a2a]"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Edit dialog — same UI as Add, seeded with the picked event */}
      <EventDialog
        open={!!editingEvent}
        onClose={() => setEditingEvent(null)}
        defaultDate={data.date}
        event={editingEvent}
        onSaved={() => { setEditingEvent(null); load(); }}
      />
    </div>
  );
}
